import { Link, useOutletContext, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronRight, Map as MapIcon, MapPin } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import { SIGNBOARDS, REGIONS } from "@/data/signboards";

/**
 * Web Mercator projection helper.
 *
 * Given a target lat/lng and a map center+zoom, returns the on-screen
 * pixel offset (in absolute Mercator pixels at that zoom) from the
 * iframe's centre. These offsets are stable regardless of the iframe's
 * rendered viewport size, because Google Maps iframes always project at
 * the requested zoom level using the same Mercator tile math we apply
 * here. We use this to overlay HTML pins exactly where each signboard
 * sits, on top of an unmarked Google base-map iframe.
 */
function mercatorPixelOffset(lat, lng, centerLat, centerLng, zoom) {
  const worldSize = 256 * 2 ** zoom;
  const toXY = (la, ln) => {
    const x = ((ln + 180) / 360) * worldSize;
    const latRad = (la * Math.PI) / 180;
    // asinh(tan(lat)) = ln(tan(lat) + sec(lat))
    const t = Math.tan(latRad);
    const sec = 1 / Math.cos(latRad);
    const merc = Math.log(t + sec);
    const y = (1 - merc / Math.PI) / 2 * worldSize;
    return { x, y };
  };
  const p = toXY(lat, lng);
  const c = toXY(centerLat, centerLng);
  return { x: p.x - c.x, y: p.y - c.y };
}

/**
 * MapDetailPage — single page that handles both /signboards/map/mainland
 * and /signboards/map/okinawa via the :scope param.
 *
 * Layout mirrors SignboardDetailPage:
 *   - Breadcrumb header
 *   - Mobile rail (horizontal pill scroller)
 *   - Two-column grid: sticky left rail + main content
 *
 * The left rail has TWO extra entries at the top (Mainland Map · Okinawa
 * Map) so visitors can hop between the two map detail pages, then the
 * standard region-grouped signboard list.
 *
 * Right side: a live Google Maps iframe centered on the relevant region
 * (no API key — uses the no-key `maps.google.com/maps?q=...&output=embed`
 * format). For Okinawa the 3 boards are listed as cards below the map.
 */

const SCOPES = {
  mainland: {
    titleEn: "Mainland Japan — Detailed Map",
    titleJa: "本州日本 — 詳細マップ",
    regionEn: "Honshu · Kanagawa",
    regionJa: "本州・神奈川",
    accent: "#0E5FB0",
    introEn:
      "Our Yokosuka signboard sits in Kanagawa Prefecture, near the U.S. Naval Base Yokosuka — NoJo Enterprise's home base since 2013. The interactive map below shows the exact geographic location.",
    introJa:
      "横須賀の看板は、米海軍横須賀基地近く、神奈川県横須賀市に設置されています。NoJo Enterprise創業の地です。下記の地図で正確な所在地をご確認いただけます。",
    embed:
      "https://maps.google.com/maps?q=35.2822315,139.6601223&hl=en&z=14&output=embed",
    boardSlugs: ["yokosuka-base"],
  },
  okinawa: {
    titleEn: "Okinawa Japan — Detailed Map",
    titleJa: "沖縄日本 — 詳細マップ",
    regionEn: "Okinawa · Route 58",
    regionJa: "沖縄・国道58号線",
    accent: "#2F8F3C",
    introEn:
      "Three signboards along Route 58 in central Okinawa — Oyama, B-36A Camp Foster, and B-13. They sit within roughly 3 km of one another along Okinawa's main north-south arterial. The single map below pins all three locations in one view.",
    introJa:
      "国道58号線沿いに、大山、B-36A キャンプ・フォスター、B-13の3区画を設置。沖縄本島を南北に貫く主要幹線道路沿い、約3km圏内に位置しています。下の1枚のマップで、3区画すべての位置をご覧いただけます。",
    // Google's no-key embed only honors `z=` when there's a query
    // marker (`q=`). With only `ll=` it auto-fits to the iframe
    // dimensions, which on mobile zooms way out and breaks our
    // overlay-pin Mercator math. So we anchor `q=` at OYAMA — the
    // geographic middle of the 3 boards — so:
    //   • Google reliably renders at zoom 14 across all viewports
    //   • Google's native marker lands ON Oyama (overlap with our
    //     Oyama overlay pin is intentional — same point)
    //   • B-36A flanks north-east, B-13 flanks south-west, with the
    //     spatial relationship preserved on every screen size.
    embed:
      "https://maps.google.com/maps?q=26.2833499,127.7535043&hl=en&z=14&output=embed",
    boardSlugs: ["b13-route-58", "b36a-camp-foster", "oyama-route-58"],
    multiPin: {
      // Center is Oyama's exact coords — matches the `q=` anchor
      // above so the Mercator offsets line up with what Google
      // actually centers and renders.
      centerLat: 26.2833499,
      centerLng: 127.7535043,
      zoom: 14,
      openMapsHref:
        "https://www.google.com/maps/@26.2833499,127.7535043,14z",
    },
  },
};

export default function MapDetailPage() {
  const { lang } = useOutletContext();
  const { scope } = useParams();
  const isJa = lang === "ja";

  if (!SCOPES[scope]) {
    return <Navigate to={`/${lang}/signboards/map/mainland`} replace />;
  }
  const conf = SCOPES[scope];

  // Localised embed URL — Google honours `hl=` for the UI language.
  const embedSrc = conf.embed.replace(/hl=[a-z-]+/i, `hl=${isJa ? "ja" : "en"}`);

  // For the left-rail and mobile-rail nav, group all signboards by region.
  const byRegion = REGIONS.map((r) => ({
    region: r,
    boards: SIGNBOARDS.filter((s) => s.region === r.key),
  })).filter((g) => g.boards.length > 0);

  // Boards on this map (used in the "Locations on this map" list).
  const scopedBoards = conf.boardSlugs
    .map((slug) => SIGNBOARDS.find((b) => b.slug === slug))
    .filter(Boolean);

  return (
    <div data-testid={`map-detail-page-${scope}`} className="bg-white">
      <SEO
        {...(scope === "okinawa"
          ? PAGE_SEO.signboardsMapOkinawa[lang]
          : PAGE_SEO.signboardsMapMainland[lang])}
        path={`/${lang}/signboards/map/${scope}`}
        lang={lang}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "看板掲出" : "Signboards", url: `/${lang}/signboards` },
          { name: isJa ? "詳細マップ" : "Detailed Maps", url: `/${lang}/signboards/map/${scope}` },
          { name: isJa ? conf.titleJa : conf.titleEn, url: `/${lang}/signboards/map/${scope}` },
        ])}
      />

      {/* ===== Breadcrumb header — single line on every viewport.
           The trail can overflow horizontally on narrow mobile so the
           current page title doesn't wrap and squish the layout. ===== */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-6 md:pt-8 pb-5">
          <div className="flex flex-nowrap items-center gap-2 text-[12px] text-[#64748B] overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Link
              to={`/${lang}/signboards`}
              data-testid="map-detail-breadcrumb-hub"
              className="inline-flex items-center gap-1.5 shrink-0 hover:text-[#0F1D2D] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              {isJa ? "看板一覧" : "All signboards"}
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50 shrink-0" strokeWidth={2} />
            <span className="font-semibold shrink-0" style={{ color: conf.accent }}>
              {isJa ? "詳細マップ" : "Detailed Maps"}
            </span>
            <ChevronRight className="h-3 w-3 opacity-50 shrink-0" strokeWidth={2} />
            <span className="text-[#0F1D2D] font-semibold shrink-0">
              {isJa ? conf.titleJa : conf.titleEn}
            </span>
          </div>
        </div>
      </section>

      {/* ===== Mobile rail ===== */}
      <div
        data-testid="map-detail-mobile-rail"
        className="md:hidden border-b border-[#E2E8F0] bg-[#F8F9FA] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex items-center gap-2 px-4 py-3 whitespace-nowrap">
          {/* Map pills — first */}
          {Object.entries(SCOPES).map(([key, c]) => {
            const active = key === scope;
            return (
              <Link
                key={key}
                to={`/${lang}/signboards/map/${key}`}
                data-testid={`map-mobile-rail-${key}`}
                style={active ? { backgroundColor: c.accent, color: "#fff" } : undefined}
                className={[
                  "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-colors shrink-0",
                  active
                    ? "shadow-sm"
                    : "bg-white border border-[#E2E8F0] text-[#475569] hover:text-[#0F1D2D]",
                ].join(" ")}
              >
                <MapIcon className="h-3 w-3" strokeWidth={2} />
                {key === "mainland" ? (isJa ? "本州マップ" : "Mainland Map") : (isJa ? "沖縄マップ" : "Okinawa Map")}
              </Link>
            );
          })}
          {/* Signboard pills */}
          {SIGNBOARDS.map((b) => {
            const bAcc = REGIONS.find((r) => r.key === b.region)?.accent || "#0F1D2D";
            return (
              <Link
                key={b.slug}
                to={`/${lang}/signboards/${b.slug}`}
                data-testid={`map-mobile-rail-${b.slug}`}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-colors shrink-0 bg-white border border-[#E2E8F0] text-[#475569] hover:text-[#0F1D2D]"
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: bAcc }} />
                {b.title[lang]}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ===== Main grid ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-5 md:pt-6 pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          {/* LEFT RAIL */}
          <aside
            className="hidden md:block md:col-span-3"
            data-testid="map-detail-left-rail"
          >
            <div className="sticky top-24">
              {/* Map pages — top of the rail */}
              <div className="text-[10px] tracking-[0.28em] font-mono font-bold text-[#64748B] uppercase mb-3">
                {isJa ? "詳細マップ" : "Detailed maps"}
              </div>
              <nav className="space-y-1 mb-7">
                {Object.entries(SCOPES).map(([key, c]) => {
                  const active = key === scope;
                  return (
                    <Link
                      key={key}
                      to={`/${lang}/signboards/map/${key}`}
                      data-testid={`map-left-rail-${key}`}
                      style={active ? { backgroundColor: c.accent } : undefined}
                      className={[
                        "block rounded-xl px-3 py-2.5 text-[13px] font-semibold tracking-tight transition-colors",
                        active
                          ? "text-white shadow-sm"
                          : "text-[#0F1D2D] hover:bg-[#F1F5F9]",
                      ].join(" ")}
                    >
                      <div className="flex items-center gap-2">
                        <MapIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                        <span className="truncate leading-tight">
                          {key === "mainland"
                            ? (isJa ? "本州マップ" : "Mainland Map")
                            : (isJa ? "沖縄マップ" : "Okinawa Map")}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </nav>

              {/* All signboards — same structure as detail page */}
              <div className="text-[10px] tracking-[0.28em] font-mono font-bold text-[#64748B] uppercase mb-3">
                {isJa ? "全看板" : "All signboards"}
              </div>
              <nav className="space-y-6">
                {byRegion.map(({ region: r, boards }) => (
                  <div key={r.key}>
                    <div
                      className="flex items-center gap-2 text-[11.5px] tracking-[0.18em] uppercase font-bold mb-2"
                      style={{ color: r.accent }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: r.accent }} />
                      {isJa ? r.ja : r.en}
                    </div>
                    <ul className="space-y-1">
                      {boards.map((b) => (
                        <li key={b.slug}>
                          <Link
                            to={`/${lang}/signboards/${b.slug}`}
                            data-testid={`map-left-rail-${b.slug}`}
                            className="group block rounded-xl px-3 py-2.5 text-[13px] font-semibold tracking-tight text-[#0F1D2D] hover:bg-[#F1F5F9] transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate leading-tight">{b.title[lang]}</span>
                              {b.route && (
                                <span className="text-[9.5px] tracking-[0.12em] uppercase font-bold shrink-0 text-[#94A3B8]">
                                  {b.route}
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>

              <Link
                to={`/${lang}/signboards`}
                data-testid="map-left-rail-hub"
                className="mt-7 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#5B2E83] hover:text-[#3F1E5C] transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                {isJa ? "看板ハブに戻る" : "Back to signboards hub"}
              </Link>
            </div>
          </aside>

          {/* MAIN CONTENT — Google Maps embed + board list */}
          <article className="md:col-span-9 min-w-0">
            {/* Condensed header — pills + compact title on a single row
                so the map appears immediately without scrolling. The
                fuller intro paragraph sits BELOW the map, where it
                provides context after the visual has already loaded. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
              <h1
                data-testid="map-detail-title"
                className="text-[20px] md:text-[24px] font-display font-bold tracking-tight text-[#0F1D2D] leading-tight"
              >
                {isJa ? conf.titleJa : conf.titleEn}
              </h1>
              <span
                className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase font-bold text-white px-2 py-0.5 rounded-full"
                style={{ backgroundColor: conf.accent }}
                data-testid="map-detail-region-pill"
              >
                {isJa ? conf.regionJa : conf.regionEn}
              </span>
            </div>

            {/* Live Google Maps embed — main overview.
                For Okinawa we LOCK the iframe (pointer-events: none)
                and overlay 3 HTML red pins at the boards' true
                lat/lng via Mercator projection — so visitors see the
                geographic relationship of all three boards at a
                glance, on BOTH desktop and mobile. On mobile we use a
                near-square aspect ratio so the pins (which span
                ~220px × ~220px at zoom 14) all fit comfortably inside
                the iframe; on desktop a wider 18:9 ratio gives the
                roadside context room to breathe.
                For Mainland (single board) the iframe stays
                interactive — Google's own pin is shown via the
                `q=lat,lng` parameter. */}
            <div
              data-testid="map-detail-google-embed"
              className="mt-2 rounded-3xl overflow-hidden ring-1 ring-[#E2E8F0] bg-[#5B2E83] shadow-[0_24px_60px_-24px_rgba(91,46,131,0.4)]"
            >
              <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 bg-[#5B2E83] text-white">
                <span className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-bold">
                  <span className="relative inline-flex h-2 w-2">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
                      style={{ backgroundColor: conf.accent }}
                    />
                    <span
                      className="relative inline-flex h-2 w-2 rounded-full"
                      style={{ backgroundColor: conf.accent }}
                    />
                  </span>
                  {conf.multiPin
                    ? isJa
                      ? `ライブ · ${scopedBoards.length}件の看板`
                      : `Live · ${scopedBoards.length} signboards`
                    : isJa
                    ? "ライブ · Google マップ"
                    : "Live · Google Maps"}
                </span>
                {!conf.multiPin && (
                  <span className="hidden md:inline-flex text-[10.5px] tracking-[0.22em] uppercase font-bold text-white/70">
                    {isJa ? "ドラッグ・ズーム可" : "Drag · zoom · explore"}
                  </span>
                )}
              </div>
              <div className="relative">
                <iframe
                  src={embedSrc}
                  title={isJa ? conf.titleJa : conf.titleEn}
                  className="w-full block aspect-square sm:aspect-[16/9] md:aspect-[18/9]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  // Lock interactivity when we're rendering custom pin
                  // overlays so the pins stay geographically aligned
                  // on every viewport (desktop and mobile alike).
                  style={conf.multiPin ? { pointerEvents: "none" } : undefined}
                />

                {/* Custom multi-pin overlay — renders on both desktop
                    and mobile. The iframe is locked above so these
                    overlay pins stay aligned with the basemap. */}
                {conf.multiPin && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    data-testid="map-detail-multi-pin-overlay"
                    aria-hidden="false"
                  >
                    {scopedBoards.map((b) => {
                      if (!b.streetView || b.streetView.lat == null) return null;
                      const { x, y } = mercatorPixelOffset(
                        b.streetView.lat,
                        b.streetView.lng,
                        conf.multiPin.centerLat,
                        conf.multiPin.centerLng,
                        conf.multiPin.zoom,
                      );
                      return (
                        <Link
                          key={b.slug}
                          to={`/${lang}/signboards/${b.slug}`}
                          data-testid={`map-detail-pin-${b.slug}`}
                          aria-label={
                            isJa
                              ? `${b.title.ja} の詳細を見る`
                              : `View ${b.title.en} detail`
                          }
                          className="pointer-events-auto absolute group"
                          style={{
                            left: `calc(50% + ${x}px)`,
                            top: `calc(50% + ${y}px)`,
                            transform: "translate(-50%, -100%)",
                          }}
                        >
                          {/* Pulse ring */}
                          <span
                            aria-hidden
                            className="absolute left-1/2 -translate-x-1/2 bottom-0 h-4 w-4 rounded-full opacity-50 animate-ping"
                            style={{ backgroundColor: "#DC2626" }}
                          />
                          {/* Pin body */}
                          <span
                            aria-hidden
                            className="relative block h-9 w-9 rounded-full bg-[#DC2626] ring-2 ring-white shadow-[0_10px_24px_-4px_rgba(220,38,38,0.65)] transition-transform group-hover:scale-110"
                            style={{
                              clipPath:
                                "polygon(50% 100%, 18% 60%, 28% 28%, 50% 16%, 72% 28%, 82% 60%)",
                            }}
                          />
                          {/* Inner dot */}
                          <span
                            aria-hidden
                            className="absolute left-1/2 -translate-x-1/2 top-[10px] h-2.5 w-2.5 rounded-full bg-white"
                          />
                          {/* Always-on label so visitors immediately
                              see which board each pin is — desktop
                              shows the full name, mobile shows the
                              short id (B-13 / B-36A / Oyama) to keep
                              the labels from overlapping each other
                              on a narrow viewport. */}
                          <span
                            className="absolute left-1/2 -translate-x-1/2 -top-7 whitespace-nowrap rounded-md bg-[#5B2E83] text-white text-[10.5px] md:text-[11px] font-semibold tracking-tight px-2 py-0.5 shadow-lg pointer-events-none"
                          >
                            <span className="hidden md:inline">{b.title[lang]}</span>
                            <span className="md:hidden">
                              {(b.title[lang] || "").split(" — ")[0]}
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}

                {/* Pin legend — small, compact chip in the bottom-right
                    corner. Sits over an empty area of the map (not
                    over the road or pins) and stays out of the way
                    on every viewport size. */}
                {conf.multiPin && (
                  <div
                    data-testid="map-detail-pin-legend"
                    className="pointer-events-none absolute bottom-2 right-2 md:bottom-3 md:right-3"
                  >
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur ring-1 ring-[#E2E8F0] shadow-[0_8px_22px_-10px_rgba(15,29,45,0.4)] px-2.5 py-1">
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full bg-[#DC2626]"
                      />
                      <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#475569]">
                        {isJa
                          ? `${scopedBoards.length}件の看板`
                          : `${scopedBoards.length} signboards`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Intro / context — moved BELOW the map per owner directive
                so the map is the immediate focal point on page load. */}
            <p className="mt-5 text-[14px] md:text-[15px] leading-[1.75] text-[#475569] max-w-3xl">
              {isJa ? conf.introJa : conf.introEn}
            </p>

            {/* Boards on this map */}
            <div className="mt-8">
              <div className="text-[10.5px] tracking-[0.28em] font-mono font-bold text-[#0F1D2D] uppercase mb-3">
                {isJa
                  ? `このマップの看板 · ${scopedBoards.length}件`
                  : `Signboards on this map · ${scopedBoards.length}`}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {scopedBoards.map((b) => {
                  const r = REGIONS.find((rg) => rg.key === b.region);
                  return (
                    <Link
                      key={b.slug}
                      to={`/${lang}/signboards/${b.slug}`}
                      data-testid={`map-detail-board-${b.slug}`}
                      className="group rounded-2xl bg-white ring-1 ring-[#E2E8F0] hover:ring-[#0F1D2D]/30 hover:shadow-[0_12px_30px_-12px_rgba(15,29,45,0.2)] transition-all p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="inline-flex items-center text-[9.5px] tracking-[0.18em] uppercase font-bold text-white px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: r?.accent }}
                        >
                          {isJa ? r?.ja : r?.en}
                        </span>
                        {b.route && (
                          <span className="text-[9.5px] tracking-[0.12em] uppercase font-bold text-[#94A3B8]">
                            {b.route}
                          </span>
                        )}
                      </div>
                      <h3 className="text-[15px] font-display font-bold tracking-tight text-[#0F1D2D] leading-tight">
                        {b.title[lang]}
                      </h3>
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-[#64748B]">
                        <MapPin className="h-3 w-3 shrink-0" strokeWidth={2} />
                        <span className="truncate">{b.location[lang]}</span>
                      </div>
                      <div
                        className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold tracking-tight"
                        style={{ color: r?.accent }}
                      >
                        {isJa ? "詳細を見る" : "View detail"}
                        <ArrowUpRight className="h-3 w-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
